import PerformanceKpiTable from './PerformanceKpiTable'
import { useWidgetContext } from '../../widgets/providers'
import { useAllTeams } from '../../teams/api'
import { useMetricsData, getMaxPeriod, type DataPoint } from '../../widgets/api'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useAllPlayers } from '../../players/api'

export default function PerformanceLadder({
  page,
  page_size,
}: {
  page?: number
  page_size?: number
} = {}) {
  const ctx = useWidgetContext()
  const metricKey = ctx.metric || 'revenue' // fallback to revenue if no metric selected
  const subject_type = ctx.subject_type || 'team'
  const component = ctx.component || 'percent'
  const statistic = ctx.statistic || 'value'

  const { data: teams } = useAllTeams({
    competition_id: ctx.competition_id || 0,
    page,
    page_size,
  })
  const { data: players } = useAllPlayers({
    competition_id: ctx.competition_id || 0,
    page,
    page_size,
  })

  const subjects =
    subject_type === 'team'
      ? teams.items.map((t) => ({
          id: t.team_id,
          name: t.name,
          logo: t.logo.thumbnail,
        }))
      : players.items.map((p) => ({
          id: p.user_id || 0,
          name: p.name,
          logo: p.photo.thumbnail,
        }))

  const subject_ids =
    subject_type === 'team'
      ? teams.items.map((t) => t.team_id)
      : players.items.map((p) => p.user_id || 0)

  // Get max year period
  const { data: maxYearData } = useSuspenseQuery({
    queryKey: ['max-year-period', subject_type, ctx.competition_id],
    queryFn: () =>
      getMaxPeriod({
        period_type: 'year',
        subject_type,
        competition_id: ctx.competition_id,
      }),
  })

  const maxYearPeriod = maxYearData?.max_period || dayjs().format('YYYY-MM-DD')
  const currentYear = maxYearPeriod.slice(0, 4)

  // Fetch year period data for all teams
  const { data: yearData } = useMetricsData({
    period_type: 'year',
    period_range: false,
    periods: [maxYearPeriod],
    subject_type,
    subject_ids,
    metrics: [metricKey],
    components: [component],
    statistics: [statistic],
    competition_id: ctx.competition_id,
  })

  // Fetch monthly data for all teams using period range
  const { data: monthlyData } = useMetricsData({
    period_type: 'month',
    period_range: true,
    periods: [`${currentYear}-01-01`, `${currentYear}-12-01`],
    subject_type,
    subject_ids,
    metrics: [metricKey],
    components: [component],
    statistics: [statistic],
    competition_id: ctx.competition_id,
  })

  // Transform data for the table
  const tableData = useMemo(() => {
    // Create a map for year values
    const yearValueMap = new Map<number, number>()
    yearData?.forEach((d: DataPoint) => {
      yearValueMap.set(d.subject, d.value)
    })

    // Create a map for monthly values
    const monthlyValueMap = new Map<string, number>()
    monthlyData?.forEach((d: DataPoint) => {
      // Extract month from period format "YYYY-MM-DD"
      const month = d.period.slice(5, 7)
      const monthKey = `${d.subject}-${month}`
      monthlyValueMap.set(monthKey, d.value)
    })

    // Transform teams data
    const transformedData = subjects.map((subject) => {
      const yearValue = yearValueMap.get(subject.id) || 0

      // Build monthly values object
      const monthlyValues: Record<string, number | undefined> = {}
      for (let i = 1; i <= 12; i++) {
        const month = String(i).padStart(2, '0')
        const monthKey = `${subject.id}-${month}`
        const value = monthlyValueMap.get(monthKey)
        monthlyValues[month] = value
      }

      return {
        ...subject,
        rank: 0, // Will be assigned after sorting
        ytd: yearValue,
        values: monthlyValues,
      }
    })

    // Sort by year value (descending) and assign initial ranks
    transformedData.sort((a, b) => b.ytd - a.ytd)
    transformedData.forEach((data, index) => {
      data.rank = index + 1
    })

    return transformedData
  }, [subjects, yearData, monthlyData])

  const rankOffset = page && page_size ? (page - 1) * page_size : 0

  return (
    <div>
      <PerformanceKpiTable
        table_type="month"
        data={tableData}
        rank_offset={rankOffset}
      />
    </div>
  )
}
